import { scoreFromStats, FULL_PPR_LEGACY } from "./projlabProjection";
import { allTeamAbbrevs, resolveTeamRows } from "./projectionSource";

/**
 * The Rankings pool: every projectable player, all 32 teams, always.
 *
 * WAS gated on `progress.state === "done"`, which meant a fresh profile ranked
 * NOTHING — the tab rendered "No projected QBs yet" until the user had hand-built
 * a team, and every surface downstream of it (position rank maps, the results-page
 * RNK badge, the CSV export) was empty for the same reason. Now resolution is per
 * TEAM through `resolveTeamRows`: the user's save if they have one — EDITS WIN,
 * unchanged — otherwise that team's house rows. A fresh profile ranks all 257
 * house players.
 *
 * `progress` is still accepted so no caller has to change, and it still belongs in
 * callers' memo deps (it changes when a team is saved, which is exactly when this
 * should recompute) — but it is NO LONGER THE GATE. Do not reintroduce a filter on
 * it; that is the bug this replaced.
 *
 * Derive-on-read is unchanged and now applies to both sources: a stored `pts` is a
 * stale artifact of whatever lens it was saved under, so stats are re-scored under
 * the active lens. Stored pts is a last resort for ancient saves with no stats
 * object (never wipe/migrate — preserve-only).
 */
export function loadAllProjectedPlayers(progress, scoring = FULL_PPR_LEGACY) {
  const all = [];
  for (const abbrev of allTeamAbbrevs()) {
    let resolved;
    try { resolved = resolveTeamRows(abbrev); } catch { continue; }
    for (const pl of resolved.rows) {
      const games = pl.games ?? 17;
      const pts = pl.stats ? scoreFromStats(pl.stats, pl.pos, scoring, games) : (pl.pts || 0);
      if (pl.player && pts > 0) {
        all.push({ ...pl, pts, games, team: abbrev, source: resolved.source });
      }
    }
  }
  return all;
}

// Positional rank maps built from the SAME pool the Rankings tab renders
// (loadAllProjectedPlayers = all 32 teams, the user's where they have one and the
// house's elsewhere, re-scored under the active lens). Ranking each starter against
// that full field makes the results-page RNK badge equal the player's row number in
// the Rankings tab exactly — not an ADP/consensus anchor or a per-team points
// estimate. Keyed `${pos}|${player}`.
//
// These are now populated on a fresh profile, where they used to be empty. The
// graceful fallback consumers keep for a missing key is still worth keeping — a
// player who is on no depth chart at all has no rank here — but it is no longer
// load-bearing for the whole first-run experience.
export function computePosRankMaps(progress, scoring = FULL_PPR_LEGACY) {
  const all = loadAllProjectedPlayers(progress, scoring);

  const byPos = { QB: [], RB: [], WR: [], TE: [] };
  all.forEach(pl => {
    if (byPos[pl.pos]) byPos[pl.pos].push(pl);
  });

  const result = {};
  Object.entries(byPos).forEach(([pos, players]) => {
    const sorted = [...players].sort((a, b) => b.pts - a.pts);
    const map = new Map();
    sorted.forEach((pl, i) => {
      map.set(`${pos}|${pl.player}`, i + 1);
    });
    result[pos] = map;
  });
  return result;
}
